/** Pure helpers for the GUS Weekly page. */

export interface StatusLike {
  run_date: string;
  status: 'sending' | 'sent' | 'failed';
  help_hub_url: string | null;
}

/** "2026-10-01" -> "Thu 1 Oct 2026". Falls back to the raw text if unparseable. */
export const formatRunDate = (iso: string): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  if (Number.isNaN(d.getTime())) return iso;
  const parts = new Intl.DateTimeFormat('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return `${get('weekday')} ${get('day')} ${get('month')} ${get('year')}`;
};

/** The sentence for the status line (without the Help Hub link). */
export const statusText = (s: StatusLike | null): string => {
  if (!s) return 'No issues yet';
  if (s.status === 'failed') return 'Last run failed — check the alert email';
  return `Last issue: ${formatRunDate(s.run_date)} · ${s.status}`;
};

/** Only a well-formed https URL is safe to link to. */
export const safeHttpsUrl = (url: string | null | undefined): string | null => {
  if (!url) return null;
  try {
    const u = new URL(url);
    return u.protocol === 'https:' ? u.toString() : null;
  } catch {
    return null;
  }
};

/** Readable text from an axios error: string detail, FastAPI [{msg}] list, or fallback. */
export const errorMessage = (err: unknown, fallback = 'Something went wrong'): string => {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const msgs = detail
      .map((d) => (typeof d === 'string' ? d : (d as { msg?: unknown })?.msg))
      .filter((m): m is string => typeof m === 'string' && m.length > 0);
    if (msgs.length) return msgs.join('; ');
  }
  return fallback;
};

export const isEmail = (v: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
